import { useCallback, useMemo } from 'react'
import { useUserPermissions } from '../../pages/company/userPermissions'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import { useAutoReload } from '../../utils/hooks/useAutoReload'
import { useLocalStorage } from '../../utils/hooks/useLocalStorage'

export const ACTIVITY_AUTO_RELOAD_INTERVALS = {
  off: 0,
  '30s': 30_000,
  '1m': 60_000,
  '5m': 300_000,
} as const

export const REPORT_AUTO_RELOAD_INTERVALS = {
  off: 0,
  '5m': 300_000,
  '10m': 600_000,
  '20m': 1_200_000,
  '30m': 1_800_000,
} as const

type AutoReloadIntervalKey<T extends Record<string, number>> = keyof T & string

export type UseAutoReloadControlOptions<T extends Record<string, number>> = {
  defaultInterval: AutoReloadIntervalKey<T>
  enabled?: boolean
  intervals: T
  onReload: () => void | Promise<void>
  storageKey: string
  translationNamespace: 'activity' | 'report'
}

export function useAutoReloadControl<T extends Record<string, number>>({
  defaultInterval,
  enabled = true,
  intervals,
  onReload,
  storageKey,
  translationNamespace,
}: UseAutoReloadControlOptions<T>) {
  const t = useAppTranslations()
  const { superAdmin } = useUserPermissions()
  const intervalKeys = useMemo(() => Object.keys(intervals) as AutoReloadIntervalKey<T>[], [intervals])
  const [autoReload, setAutoReload] = useLocalStorage(storageKey, { interval: defaultInterval })

  const selectedInterval = intervalKeys.includes(autoReload.interval as AutoReloadIntervalKey<T>)
    ? (autoReload.interval as AutoReloadIntervalKey<T>)
    : defaultInterval
  const intervalMs = intervals[selectedInterval] ?? 0

  useAutoReload({
    enabled: superAdmin && enabled && intervalMs > 0,
    intervalMs,
    onReload,
  })

  const getIntervalLabel = useCallback(
    (intervalKey: AutoReloadIntervalKey<T>): string => {
      if (intervalKey === 'off') return t('commons.autoReload.off')
      return t(`${translationNamespace}.autoReload.${intervalKey}`)
    },
    [t, translationNamespace]
  )

  const onIntervalChange = useCallback((interval: string) => setAutoReload({ interval }), [setAutoReload])

  return {
    getIntervalLabel,
    intervalKeys,
    onIntervalChange,
    selectedInterval,
    storageKey,
    superAdmin,
  }
}
